import React from 'react';
import { MechanicalContractorFormData, MechanicalContractorFormErrors } from './dialogTypes';
import { POSTAL_CODE_PROVINCE_MAP } from '../AddressStep';

export const validateStep1 = (
  formData: MechanicalContractorFormData,
  setErrors: React.Dispatch<React.SetStateAction<MechanicalContractorFormErrors>>
): boolean => {
  const newErrors: MechanicalContractorFormErrors = {
    name: '',
    status: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    localTradeSelections: ''
  };

  let isValid = true;

  // Validate contractor name
  if (!formData.name.trim()) {
    newErrors.name = 'Contractor name is required';
    isValid = false;
  }

  // Validate status
  if (!formData.status) {
    newErrors.status = 'Status is required';
    isValid = false;
  }

  setErrors(newErrors);
  return isValid;
};

export const validateStep2 = (
  formData: MechanicalContractorFormData,
  setErrors: React.Dispatch<React.SetStateAction<MechanicalContractorFormErrors>>,
  provinces: { id: string; name: string }[] = []
): boolean => {
  const newErrors: MechanicalContractorFormErrors = {
    name: '',
    status: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    localTradeSelections: ''
  };

  let isValid = true;

  const hasSubOffices = formData.subOffices && formData.subOffices.length > 0 &&
                        formData.subOffices.some(office =>
                          office.address?.trim() || office.city?.trim() || office.province || office.postalCode?.trim()
                        );
  const hasHeadOffice = formData.address?.trim() || formData.city?.trim() || formData.province || formData.postalCode?.trim();

  if (hasSubOffices && !hasHeadOffice) {
    newErrors.address = 'Head office address is required when sub-offices are provided';
    isValid = false;
  }

  const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

  // Head office postal code validation
  if (formData.postalCode && formData.postalCode.trim()) {
    if (!postalCodeRegex.test(formData.postalCode.trim())) {
      newErrors.postalCode = 'Please enter a valid Canadian postal code (e.g., A1A 1A1)';
      isValid = false;
    } else if (formData.province) {
      const prefix = formData.postalCode.trim()[0].toUpperCase();
      const validProvinces = POSTAL_CODE_PROVINCE_MAP[prefix];
      const selectedProvince = provinces.find(p => p.id === formData.province);
      if (validProvinces && selectedProvince && !validProvinces.includes(selectedProvince.name)) {
        newErrors.postalCode = `This postal code does not belong to ${selectedProvince.name}`;
        isValid = false;
      }
    }
  }

  // Sub office postal code validation
  if (formData.subOffices && formData.subOffices.length > 0) {
    newErrors.subOffices = [];

    formData.subOffices.forEach((office, index) => {
      const subOfficeErrors: { address?: string; city?: string; province?: string; postalCode?: string } = {};

      if (office.postalCode && office.postalCode.trim()) {
        if (!postalCodeRegex.test(office.postalCode.trim())) {
          subOfficeErrors.postalCode = 'Please enter a valid Canadian postal code (e.g., A1A 1A1)';
          isValid = false;
        } else if (office.province) {
          const prefix = office.postalCode.trim()[0].toUpperCase();
          const validProvinces = POSTAL_CODE_PROVINCE_MAP[prefix];
          const selectedProvince = provinces.find(p => p.id === office.province);
          if (validProvinces && selectedProvince && !validProvinces.includes(selectedProvince.name)) {
            subOfficeErrors.postalCode = `This postal code does not belong to ${selectedProvince.name}`;
            isValid = false;
          }
        }
      }

      newErrors.subOffices![index] = subOfficeErrors;
    });
  }

  setErrors(newErrors);
  return isValid;
};

export const validateFormData = (
  formData: MechanicalContractorFormData,
  setErrors: React.Dispatch<React.SetStateAction<MechanicalContractorFormErrors>>
): boolean => {
  // Run both step validations
  const step1Valid = validateStep1(formData, setErrors);
  const step2Valid = validateStep2(formData, setErrors);

  return step1Valid && step2Valid;
};